import { Component, Input, OnInit, TemplateRef, inject } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { NzSpinModule } from "ng-zorro-antd/spin";
import { NzSelectModule } from "ng-zorro-antd/select";
import { NzTypographyModule } from 'ng-zorro-antd/typography';
import { NzDropDownModule } from 'ng-zorro-antd/dropdown';
import { NzInputModule } from 'ng-zorro-antd/input';
import { CommonModule } from "@angular/common";
import { NzButtonModule } from "ng-zorro-antd/button";
import { NzIconModule } from "ng-zorro-antd/icon";
import { NzTableModule } from "ng-zorro-antd/table";
import { FormsModule } from "@angular/forms";
import { NzFormModule } from "ng-zorro-antd/form";
import { NzModalRef, NzModalService, NzModalModule } from 'ng-zorro-antd/modal';
import { NzMessageService } from 'ng-zorro-antd/message';
import { NzTagModule } from 'ng-zorro-antd/tag';
import { NzSkeletonModule } from 'ng-zorro-antd/skeleton';
import { NzToolTipModule } from 'ng-zorro-antd/tooltip';
import { NzDatePickerModule } from 'ng-zorro-antd/date-picker';
import { take } from 'rxjs';
import { formatISO } from 'date-fns';
import { QuickfiltersService } from '../../services/quickfilter/quickfilters.service';
import { VariablesService } from '../../services/variables/variables.service';

export interface QuickFilterCondition {
  field: string | null;
  headerName: string;
  filterType: string | null;
  filterOptions: string[];
  selectedOption: string | null;
  value: any;
  valueTo: any;
  values: string[];
}

@Component({
  selector: 'quickfilters',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    NzSpinModule,
    NzSelectModule,
    NzTypographyModule,
    NzDropDownModule,
    NzInputModule,
    NzButtonModule,
    NzIconModule,
    NzTableModule,
    NzFormModule,
    NzTagModule,
    NzSkeletonModule,
    NzToolTipModule,
    NzModalModule,
    NzDatePickerModule,
  ],
  templateUrl: './quickfilters.component.html',
  styleUrl: './quickfilters.component.scss'
})
export class QuickfiltersComponent implements OnInit {
  private quickfiltersService = inject(QuickfiltersService);
  private variablesService = inject(VariablesService);
  private modal = inject(NzModalService);
  private message = inject(NzMessageService);
  private route = inject(ActivatedRoute);

  @Input() filterType: 'quickfilter' | 'standardfilter' = 'quickfilter';

  quickfilters: any[] = [];
  columnDefinition: any[] = [];
  isLoading = true;
  isCRUD = false;

  editingFilter: any = null;
  filterName = '';

  workingConditions: QuickFilterCondition[] = [];
  selectedMatchingRules: string[] = [];

  readonly matchingRuleOptions = [
    { value: 'cardealer', label: 'MA' },
    { value: 'leasing', label: 'PLC' },
    { value: 'fleet', label: 'PFM' },
  ];

  get filterTitle(): string {
    return this.filterType === 'quickfilter' ? 'Quickfilter' : 'Standardfilter';
  }

  get isStandardFilter(): boolean {
    return this.filterType === 'standardfilter';
  }

  get filterTitleLower(): string {
    return this.filterType === 'quickfilter' ? 'quickfilter' : 'standardfilter';
  }

  ngOnInit(): void {
    this.route.url.subscribe(() => {
      const path = this.route.snapshot.routeConfig?.path || '';
      this.filterType = path === 'standardfilter' ? 'standardfilter' : 'quickfilter';
    });

    this.variablesService.getColumnDefinitions()
      .subscribe((columns: any[]) => {
        this.columnDefinition = columns.sort((a, b) =>
          (a.headerName ?? a.field ?? '').localeCompare(b.headerName ?? b.field ?? '')
        );
      });
    this.loadQuickfilters();
  }

  loadQuickfilters(): void {
    this.isLoading = true;
    (this.quickfiltersService.getAllFilters(this.filterType) as any)
      .pipe(take(1))
      .subscribe({
        next: (res: any[]) => {
          this.quickfilters = res || [];
          this.isLoading = false;
        },
        error: () => { this.isLoading = false; }
      });
  }

  private emptyCondition(): QuickFilterCondition {
    return {
      field: null,
      headerName: '',
      filterType: null,
      filterOptions: [],
      selectedOption: null,
      value: null,
      valueTo: null,
      values: []
    };
  }

  openModal(content: TemplateRef<{}>, footer: TemplateRef<{}>, existing?: any): void {
    if (existing) {
      this.editingFilter = existing;
      this.filterName = existing.name;
      this.workingConditions = existing.filter;
      this.selectedMatchingRules = [...(existing.matching_rules ?? [])];
    } else {
      this.editingFilter = null;
      this.filterName = '';
      this.workingConditions = [this.emptyCondition()];
      this.selectedMatchingRules = [];
    }

    this.modal.create({
      nzTitle: existing ? `Edit ${this.filterTitleLower}` : `Add ${this.filterTitleLower}`,
      nzContent: content,
      nzFooter: footer,
      nzMaskClosable: false,
      nzWidth: 700
    });
  }

  onFieldSelect(condition: QuickFilterCondition): void {
    const column = this.columnDefinition.find((c: any) => c.field === condition.field);
    if (!column) return;

    condition.headerName = column.headerName ?? '';
    condition.filterType = column.filter ?? null;
    condition.value = null;
    condition.valueTo = null;
    condition.values = [];
    condition.selectedOption = null;

    if (column.filter === 'agSetColumnFilter') {
      condition.filterOptions = [];
      condition.selectedOption = 'set';
    } else {

      condition.filterOptions = column.filterParams?.filterOptions ?? this.defaultOptions(column.filter);
      condition.selectedOption = condition.filterOptions[0] ?? null;
    }
  }

  private defaultOptions(filterType: string): string[] {
    switch (filterType) {
      case 'agTextColumnFilter':
        return ['contains', 'equals', 'blank', 'notBlank'];
      case 'agDateColumnFilter':
        return ['inRange', 'inDays', 'inPastDays', 'blank', 'notBlank'];
      case 'agNumberColumnFilter':
        return ['inRange', 'blank', 'notBlank'];
      default:
        return [];
    }
  }

  addCondition(): void {
    this.workingConditions = [...this.workingConditions, this.emptyCondition()];
  }

  removeCondition(index: number): void {
    this.workingConditions.splice(index, 1);
    if (this.workingConditions.length === 0) {
      this.workingConditions = [this.emptyCondition()];
    }
  }

  addSetValue(condition: QuickFilterCondition, input: HTMLInputElement): void {
    const val = input.value.trim();
    if (!val) return;
    condition.values = [...condition.values, val];
    input.value = '';
  }

  removeSetValue(condition: QuickFilterCondition, value: string): void {
    condition.values = condition.values.filter(v => v !== value);
  }

  isConditionValid(condition: QuickFilterCondition): boolean {
    if (!condition.field || !condition.filterType) return false;

    if (condition.filterType === 'agSetColumnFilter') {
      return condition.values.length > 0;
    }

    if (!condition.selectedOption) return false;

    if (condition.selectedOption === 'blank' || condition.selectedOption === 'notBlank') {
      return true;
    }

    switch (condition.filterType) {
      case 'agTextColumnFilter':
        return typeof condition.value === 'string' && condition.value.trim() !== '';
      case 'agDateColumnFilter':
        if (condition.selectedOption === 'inRange') {
          return condition.value != null && condition.valueTo != null;
        }
        return (condition.selectedOption === 'inDays' || condition.selectedOption === 'inPastDays') ? condition.value != null : false;
      case 'agNumberColumnFilter':
        return condition.selectedOption === 'inRange'
          ? condition.value != null && condition.valueTo != null
          : false;
      default:
        return false;
    }
  }

  isMainSaveDisabled(): boolean {
    if (!this.filterName.trim()) return true;
    if (this.workingConditions.length === 0) return true;
    if (this.isStandardFilter && this.selectedMatchingRules.length === 0) return true;
    return !this.workingConditions.every(c => this.isConditionValid(c));
  }

  saveQuickfilter(ref: NzModalRef): void {
    if (this.isMainSaveDisabled()) return;
    this.isCRUD = true;

    const body = this.isStandardFilter
      ? {
          filter: this.workingConditions,
          attributes: this.editingFilter?.attributes ?? [],
          matching_rules: this.selectedMatchingRules
        }
      : this.workingConditions;

    (this.quickfiltersService.postFilter(this.filterName.trim(), body, this.filterType) as any)
      .pipe(take(1))
      .subscribe({
        next: () => {
          this.message.success(`${this.filterTitle} successfully saved`);
          this.isCRUD = false;
          ref.close();
          this.loadQuickfilters();
        },
        error: () => { this.isCRUD = false; }
      });
  }

  deleteQuickfilter(name: string): void {
    this.modal.confirm({
      nzTitle: `Delete ${this.filterTitleLower}?`,
      nzContent: `This ${this.filterTitleLower} will be permanently removed.`,
      nzOkDanger: true,
      nzOkText: 'Delete',
      nzCancelText: 'Cancel',
      nzOnOk: () => {
        this.isCRUD = true;
        (this.quickfiltersService.deleteFilter(name, this.filterType) as any)
          .pipe(take(1))
          .subscribe({
            next: () => {
              this.message.success(`${this.filterTitle} deleted`);
              this.isCRUD = false;
              this.loadQuickfilters();
            },
            error: () => { this.isCRUD = false; }
          });
      }
    });
  }

  closeModal(ref: NzModalRef): void {
    ref.destroy();
  }

  getFilterOptionLabel(option: string): string {
    switch (option) {
      case 'contains': return 'Contains';
      case 'equals': return 'Equals';
      case 'blank': return 'Empty';
      case 'notBlank': return 'Not empty';
      case 'inRange': return 'Between';
      case 'inDays': return 'In next days';
      case 'inPastDays': return 'In past days';
      default: return option;
    }
  }

  getMatchingRuleLabel(rule: string): string {
    return this.matchingRuleOptions.find(o => o.value === rule)?.label ?? rule;
  }

  getFilterSummary(filter: any[]): string {
    return filter.map((obj: any) => {
      // const label = this.columnDefinition.find((c: any) => c.field === obj.attribute)?.headerName ?? obj.attribute;
      const label = obj.headerName;
      if (obj.values?.length) return `${label}: ${obj.values.join(', ')}`;
      if (obj.wildcard) return `${label} contains: ${obj.wildcard}`;
      if (obj.start) return `${label}: ${obj.start} – ${obj.end}`;
      if (obj.from != null) return `${label}: ${obj.from} – ${obj.to}`;
      if (obj.not_empty) return `${label}: not empty`;
      if (obj.value && obj.selectedOption == "inDays") return `${label}: in next ${obj.value} days`;
      if (obj.value && obj.selectedOption == "inPastDays") return `${label}: in past ${obj.value} days`;
      return `${label}: empty`;
    }).join(' & ');
  }
}
